import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Calendar, Store, Coffee, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { locations } from '../mockData';
import { FootballSubMark, RacquetSubMark } from '../components/SportBlocLogo';

const Home = () => {
  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=1600)',
            filter: 'brightness(0.3)'
          }}
        />
        
        {/* Content */}
        <div className="relative z-10 text-center px-4 max-w-5xl mx-auto">
          <div className="inline-block mb-6 px-6 py-2 bg-yellow-400/20 border border-yellow-400 rounded-full">
            <span className="text-yellow-400 font-semibold uppercase tracking-wider text-sm">Experience</span>
          </div>
          
          <h1 className="text-6xl md:text-8xl font-bold text-white mb-6">
            SPORTS
          </h1>
          
          <div className="flex items-center justify-center mb-8">
            <FootballSubMark size={64} />
            <div className="h-0.5 w-24 bg-yellow-400 mx-4"></div>
            <div className="text-3xl md:text-4xl font-light text-white tracking-widest">LIKE NEVER BEFORE</div>
            <div className="h-0.5 w-24 bg-yellow-400 mx-4"></div>
            <RacquetSubMark size={64} />
          </div>

          <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
            Premium sports facilities across multiple locations. Experience futsal and pickleball 
            at their finest with state-of-the-art arenas designed for champions.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/book">
              <Button size="lg" className="bg-yellow-400 hover:bg-yellow-500 text-black font-bold text-lg px-8 py-6">
                Book Now
              </Button>
            </Link>
            <Link to="/locations">
              <Button size="lg" variant="outline" className="border-2 border-yellow-400 text-yellow-400 hover:bg-yellow-400 hover:text-black font-bold text-lg px-8 py-6">
                View Locations
              </Button>
            </Link>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-yellow-400 rounded-full flex justify-center pt-2">
            <div className="w-1 h-3 bg-yellow-400 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Our Locations */}
      <section className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block h-1 w-20 bg-yellow-400 mb-6"></div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">OUR LOCATIONS</h2>
            <p className="text-gray-400 text-lg">Choose your arena and experience excellence</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {locations.map((location) => (
              <Card key={location.id} className="bg-gray-900 border-yellow-500/20 overflow-hidden hover:border-yellow-400 transition-all duration-300 group">
                <CardContent className="p-0">
                  <div className="relative h-64 overflow-hidden">
                    <img 
                      src={location.image} 
                      alt={location.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
                    <div className="absolute bottom-4 left-4 flex items-end gap-3">
                      {location.id === 1
                        ? <FootballSubMark size={56} />
                        : <RacquetSubMark size={56} />}
                      <div>
                        <h3 className="text-2xl font-bold text-white">{location.name}</h3>
                        <p className="text-yellow-400 font-semibold">{location.sport}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <div className="flex items-center text-gray-400 mb-4">
                      <MapPin className="w-4 h-4 mr-2 text-yellow-400" />
                      <span className="text-sm">{location.address}</span>
                    </div>
                    
                    <p className="text-gray-300 mb-6">{location.description}</p>

                    <div className="grid grid-cols-2 gap-3 mb-6">
                      {location.features.slice(0, 4).map((feature, idx) => (
                        <div key={idx} className="flex items-start">
                          <CheckCircle className="w-4 h-4 text-yellow-400 mr-2 mt-0.5 flex-shrink-0" />
                          <span className="text-xs text-gray-400">{feature}</span>
                        </div>
                      ))}
                    </div>

                    <Link to={`/locations/${location.id}`}>
                      <Button className="w-full bg-yellow-400 hover:bg-yellow-500 text-black font-semibold">
                        View Details <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Our Programs */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block h-1 w-20 bg-yellow-400 mb-6"></div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">OUR PROGRAMS</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="bg-gray-900 border-yellow-500/20 hover:border-yellow-400 transition-all duration-300 group">
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 mx-auto mb-6 bg-yellow-400/10 rounded-full flex items-center justify-center group-hover:bg-yellow-400/20 transition-colors">
                  <Calendar className="w-10 h-10 text-yellow-400" />
                </div>
                <h3 className="text-xl font-bold text-yellow-400 mb-3">Book Slot</h3>
                <p className="text-gray-400 text-sm">
                  Book your slots online and get notifications on booking confirmations straight to your phone.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gray-900 border-yellow-500/20 hover:border-yellow-400 transition-all duration-300 group">
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 mx-auto mb-6 bg-yellow-400/10 rounded-full flex items-center justify-center group-hover:bg-yellow-400/20 transition-colors">
                  <Store className="w-10 h-10 text-yellow-400" />
                </div>
                <h3 className="text-xl font-bold text-yellow-400 mb-3">Sports Store</h3>
                <p className="text-gray-400 text-sm">
                  Buy or rent equipment, shoes, and gear. Pre-order online and collect at the venue.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gray-900 border-yellow-500/20 hover:border-yellow-400 transition-all duration-300 group">
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 mx-auto mb-6 bg-yellow-400/10 rounded-full flex items-center justify-center group-hover:bg-yellow-400/20 transition-colors">
                  <Coffee className="w-10 h-10 text-yellow-400" />
                </div>
                <h3 className="text-xl font-bold text-yellow-400 mb-3">Food & Drink</h3>
                <p className="text-gray-400 text-sm">
                  Enjoy refreshments at our in-house cafe with nutritious food and beverages.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-yellow-600 to-yellow-400">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-black mb-4">Ready to Play?</h2>
          <p className="text-xl text-gray-900 mb-8">
            Book your slot now and experience sports like never before!
          </p>
          <Link to="/book">
            <Button size="lg" className="bg-black hover:bg-gray-900 text-yellow-400 font-bold text-lg px-12 py-6">
              Book Your Slot Now
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;